import { Component, inject, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CommonService, BankListDatum } from '@core/services/common.service';
import { LoadingService } from '@core/services/loading.service';
import { NotificationFacade } from '@core/facades/notification.facade';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { ButtonDirective } from '@shared/directives/button.directive';
import { InputComponent } from '@shared/ui/input/input.component';
import { TruncatePipe } from '@shared/pipes/truncate.pipe';
import { Subscription, take } from 'rxjs';
import { Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngxs/store';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { IFILTER } from 'app/app.constants';
import { TableActions } from 'app/auth/table-store/table.actions';
import { Pagination } from '@core/models/pagination.model';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';

@Component({
  selector: 'app-bank-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonDirective,
    InputComponent,
    TruncatePipe,
    RouterOutlet,
    FiltersComponent,
    TableNavigatorComponent,
    TableRefreshComponent
  ],
  templateUrl: './bank-settings.component.html',
  styleUrl: './bank-settings.component.scss'
})
export class BankSettingsComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private commonService = inject(CommonService);
  private notification = inject(NotificationFacade);
  private loadingService = inject(LoadingService);
  private cdr = inject (ChangeDetectorRef);
  private breadcrumbService = inject(BreadCrumbService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private store = inject(Store);

  bankList$ = this.store.select(TableSelectors.getTableData('banks'));
  totalCount$ = this.store.select(TableSelectors.getTableTotalCount('banks'));
  isFetchingBanks$ = this.store.select(TableSelectors.getTableLoading('banks'));

  isLoadingBanks = false;

  // Inline edit state
  editingRowId: string | null = null;
  isAddingRow = false;
  
  rowForms: Record<string, FormGroup> = {};
  latestBankMap: Record<string, { bankName: string, providus_code: string, paystack_code: string }> = {};

  addBankForm = this.fb.group({
    bank_name: ['', Validators.required],
    providusCode: [''],
    paystackCode: ['']
  });

  isUpdatingBank$ = this.loadingService.getLoadingObservable('update-bank');
  isCreatingBank$ = this.loadingService.getLoadingObservable('create-bank');

  subs: Subscription[] = [];

  isFilterActive = false;
  hadFilterBefore = false;

  filterStructure: IFILTER[] = [
    {
      filterKey: 'search',
      label: 'Search Banks',
      type: 'text',
    }
  ];

  ngOnInit(): void {
    this.breadcrumbService.setPageBreadCrumb(this.activatedRoute);
    this.loadBankList();
    this.bankFiltersEventListener();
    this.listenToBankData();
  }

  listenToBankData() {
    const sub = this.bankList$.subscribe(banks => {
      if (banks) {
        this.buildRowForms(banks);
        this.cdr.detectChanges();
      }
    });
    this.subs.push(sub);
  }

  bankFiltersEventListener() {
    this.activatedRoute.queryParams.subscribe((params) => {
      let query = '';

      if (params['search']) {
        query += `search_text=${params['search']}&`;
      }

      // Handle pagination
      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (query) {
        query += `skip=${skip}&take=${take}`;
        this.isFilterActive = true;
        this.hadFilterBefore = true; // filters are currently active

        this.store.dispatch(new TableActions.SetTableFilters({
          tableKey: 'banks',
          filters: [{ key: 'search', value: params['search'] }]
        }));

        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'banks',
            params: new Pagination({ skip: skip, size: take }),
          })
        );
      } else {
        this.isFilterActive = false;
  
        // only re-fetch if there was a previous filter
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          
          this.store.dispatch(new TableActions.SetTableFilters({
            tableKey: 'banks',
            filters: [{ key: 'search', value: '' }]
          }));

          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'banks',
              params: new Pagination({ skip: skip, size: take }),
            })
          );
        }
      }
    });
  }

  loadBankList(page = 1): void {
    const sub = this.bankList$.pipe(take(1)).subscribe((banks) => {
      const hasData = Array.isArray(banks) && banks.length > 0;
      if (!hasData) {
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'banks',
            params: new Pagination({
              skip: (page - 1) * 10,
              size: 10,
            }),
          })
        );
      }
    });
    this.subs.push(sub);
  }

  private buildRowForms(banks: BankListDatum[]): void {
    banks.forEach((bank) => {
      const bName = bank.bankName ?? '';
      const pCode = bank.providus_code ?? '';
      const psCode = bank.paystack_code ?? '';

      if (!this.rowForms[bank.bankCode]) {
        this.rowForms[bank.bankCode] = this.fb.group({
          bankName: [bName, Validators.required],
          providusCode: [pCode],
          paystackCode: [psCode]
        });
      } else if (this.editingRowId !== bank.bankCode) {
        this.rowForms[bank.bankCode].patchValue({ 
          bankName: bName,
          providusCode: pCode, 
          paystackCode: psCode 
        }, { emitEvent: false });
      }
      this.latestBankMap[bank.bankCode] = { bankName: bName, providus_code: pCode, paystack_code: psCode };
    });
  }

  enableEdit(id: string): void {
    this.editingRowId = id;
  }

  isEditing(id: string): boolean {
    return this.editingRowId === id;
  }

  closeEdit(): void {
    if (this.editingRowId) {
      this.cancelEdit(this.editingRowId);
    }
    if (this.isAddingRow) {
      this.cancelAdd();
    }
  }

  cancelEdit(id: string): void {
    const group = this.rowForms[id];
    if (!group) return;
    const latest = this.latestBankMap[id];
    if (latest) {
      group.patchValue({ 
        bankName: latest.bankName,
        providusCode: latest.providus_code, 
        paystackCode: latest.paystack_code 
      }, { emitEvent: false });
    }
    group.markAsPristine();
    group.markAsUntouched();
    this.editingRowId = null;
  }

  saveRow(bank: BankListDatum): void {
    const group = this.rowForms[bank.bankCode];
    if (!group || group.invalid) return;

    const pCode = String(group.value.providusCode).trim();
    const psCode = String(group.value.paystackCode).trim();
    
    const bName = String(group.value.bankName).trim().toUpperCase();
    const payload: any = { 
      bank_name: bName,
      bank_code_id: bank.bankCode
    };
    if (pCode) payload.providus_code = pCode;
    if (psCode) payload.paystack_code = psCode;

    const sub = this.commonService.updateBank(payload).subscribe({
      next: () => {
        this.notification.success('Bank updated successfully');
        this.editingRowId = null;
        this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
          const page = params['page'] ? parseInt(params['page']) : 1;
          const skip = (page - 1) * 10;
          this.store.dispatch(new TableActions.LoadTableData({
            tableKey: 'banks',
            params: new Pagination({ skip, size: 10 }),
          }));
        });
      },
      error: () => this.notification.error('Failed to update bank')
    });
    this.subs.push(sub);
  }

  newBankId = '';

  startAdd(): void {
    this.isAddingRow = true;
    this.newBankId = crypto.randomUUID();
    this.addBankForm.reset({
      bank_name: '',
      providusCode: '',
      paystackCode: ''
    });
  }

  cancelAdd(): void {
    this.isAddingRow = false;
  }

  saveNewBank(): void {
    if (this.addBankForm.invalid) return;
    
    const formValue = this.addBankForm.value;
    const payload: any = {
      bank_name: String(formValue.bank_name || '').trim().toUpperCase()
    };

    if (formValue.providusCode?.trim()) {
      payload.providus_code = formValue.providusCode.trim();
    }
    if (formValue.paystackCode?.trim()) {
      payload.paystack_code = formValue.paystackCode.trim();
    }

    const sub = this.commonService.createBank(payload).subscribe({
      next: () => {
        this.notification.success('Bank created successfully');
        this.editingRowId = null;
        this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
          const page = params['page'] ? parseInt(params['page']) : 1;
          const skip = (page - 1) * 10;
          this.store.dispatch(new TableActions.LoadTableData({
            tableKey: 'banks',
            params: new Pagination({ skip, size: 10 }),
          }));
        });
      },
      error: () => this.notification.error('Failed to create bank')
    });
    this.subs.push(sub);
  }

  navigate(page: number) {
    this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { ...params, page: page },
        queryParamsHandling: 'merge'
      });
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }
}